import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { services } from '../utilities/services';
import { useParams } from 'react-router-dom';

// Color theme
const colors = {
  'golden-yellow': '#E2B010',
  'sage': '#A1ADA5',
  'charcol': '#434333',
  'softwhite': '#EBF1F1',
  'palegold': '#E2B949',
  'earthybeige': '#C09C73',
};

type ContentSections = 'symptoms' | 'treatments' | 'prevention' | 'risks' | 'diagnosis';

interface ContentMap {
  symptoms: string[];
  treatments: string[];
  prevention: string[];
  risks: string[];
  diagnosis: string[];
}

type TabLabels = Record<ContentSections, string>;

interface ContentSectionProps {
  title: string;
  items: string[];
}

interface TabButtonProps {
  id: ContentSections;
  label: string;
}

const ServiceTemplate = () => {
  const [activeTab, setActiveTab] = useState<ContentSections>('symptoms');
  const { id } = useParams();
  const service = services.find(s => s.id === id) || services[0];

 

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const ContentSection: React.FC<ContentSectionProps> = ({ title, items }) => (
    <motion.div 
      className="mb-6"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={fadeIn}
      transition={{ duration: 0.3 }}
    >
      <h3 className="text-xl font-semibold mb-3" style={{ color: colors.charcol }}>{title}</h3>
      <ul className="space-y-2">
        {items.map((item: string, index: number) => (
          <motion.li 
            key={index}
            className="flex items-start"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <span style={{ color: colors['golden-yellow'] }} className="mr-2">•</span>
            <span style={{ color: colors.charcol }}>{item}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );

  const TabButton: React.FC<TabButtonProps> = ({ id, label }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors`}
      style={{
        backgroundColor: activeTab === id ? colors['golden-yellow'] : colors.sage,
        color: activeTab === id ? colors.softwhite : colors.charcol
      }}
    >
      {label}
    </motion.button>
  );

  const contentMap: ContentMap = {
    symptoms: service.content.symptoms,
    treatments: service.content.treatments,
    prevention: service.content.preventiveMeasures,
    risks: service.content.riskFactors,
    diagnosis: service.content.diagnosis
  };

  const tabLabels: TabLabels = {
    symptoms: 'Symptoms',
    treatments: 'Treatments',
    prevention: 'Prevention',
    risks: 'Risk Factors',
    diagnosis: 'Diagnosis'
  };

  return (
    <motion.div 
      style={{ backgroundColor: colors.softwhite }}
      className="min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <motion.header 
        style={{ backgroundColor: colors.earthybeige }}
        className="shadow-sm"
        variants={fadeIn}
        initial="initial"
        animate="animate"
      >
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <motion.h1 
            className="text-3xl font-bold"
            style={{ color: colors.softwhite }}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {service.title}
          </motion.h1>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <motion.div 
          className="rounded-xl shadow-md overflow-hidden"
          style={{ backgroundColor: colors.softwhite }}
          variants={fadeIn}
          initial="initial"
          animate="animate"
          transition={{ delay: 0.3 }}
        >
          {/* Top Section with Large Image and Description */}
          <div className="flex flex-col lg:flex-row">
            <motion.div 
              className="lg:w-1/2"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <img
                className="w-full h-96 object-cover lg:h-[600px]"
                src={service.imageUrl}
                alt={service.title}
              />
            </motion.div>
            <motion.div 
              className="p-8 lg:w-1/2"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-lg leading-relaxed" style={{ color: colors.charcol }}>
                {service.description}
              </p>
            </motion.div>
          </div>

          {/* Tabs Navigation */}
          <div style={{ borderColor: colors.sage }} className="border-t">
            <div className="p-4 space-x-2 overflow-x-auto whitespace-nowrap">
              {(Object.entries(tabLabels) as [ContentSections, string][]).map(([id, label]) => (
                <TabButton key={id} id={id} label={label} />
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-8" style={{ borderColor: colors.sage, borderTopWidth: '1px' }}>
            <AnimatePresence mode="wait">
              <ContentSection 
                key={activeTab}
                title={tabLabels[activeTab]} 
                items={contentMap[activeTab]} 
              />
            </AnimatePresence>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
};

export default ServiceTemplate;